import type { Breadcrumb, Event } from "@sentry/react";
import { scrubJwt } from "./scrub-jwt";

const FILTERED = "[Filtered]";
const MAX_DEPTH = 12;

const SENSITIVE_QUERY_PARAM_PATTERN = /([?&](?:token|email|api_key)=)[^&]*/gi;

// `[^\\]` must not become `.`: the ambiguous alternation backtracks exponentially.
// The trailing lookahead must stay: without it a quote inside the value ends the match early and leaks its tail.
const SENSITIVE_JSON_FIELD_PATTERN =
  /(\\?")(password|newPassword|new_password|confirmPassword|pwd|passwd|token|resetToken|accessToken|refreshToken|api_key|apiKey|secret|otp|authorization)\1(\s*:\s*)\1(?:\\.|[^\\])*?\1(?=\s*[,}\]]|$)/gi;

// email is deliberately absent: identifyUser sends user.email to Sentry outside production on purpose
const SENSITIVE_KEY_PATTERN =
  /^(?:authorization|cookie|cookies|set-cookie|password|newpassword|new_password|confirmpassword|pwd|passwd|token|resettoken|accesstoken|refreshtoken|api_key|apikey|secret|otp)$/i;

export function scrubString(value: string): string {
  return scrubJwt(
    value
      .replace(SENSITIVE_QUERY_PARAM_PATTERN, `$1${FILTERED}`)
      .replace(SENSITIVE_JSON_FIELD_PATTERN, `$1$2$1$3$1${FILTERED}$1`)
  );
}

function isTraversable(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === "string") {
    return scrubString(value);
  }

  if (depth >= MAX_DEPTH || !isTraversable(value)) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(function scrubItem(item) {
      return scrubValue(item, depth + 1);
    });
  }

  return scrubRecord(value, depth + 1);
}

function scrubRecord<T extends object>(record: T, depth: number): T {
  return Object.fromEntries(
    Object.entries(record).map(function scrubEntry([key, value]) {
      return [key, SENSITIVE_KEY_PATTERN.test(key) ? FILTERED : scrubValue(value, depth)];
    })
  ) as T;
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  return scrubRecord(breadcrumb, 0);
}

export function scrubEvent<T extends Event>(event: T): T {
  return scrubRecord(event, 0);
}
